import React, { FC, useContext, useEffect } from 'react';
import { ConfigProvider, App as AntdApp } from 'antd';
import Routes from './routes/Routes';
import { ThemeContext } from './contexts/ThemeContext';
import { useCurrentUser, UserProvider } from './contexts/UserContext';
import { ThemeColorContext } from './contexts/ThemeColorContext';
import './App.css';
import { useViewerStore } from './store/store';
import { installAiResultCacheTabLifecycle } from './services/geminiService';
import { UnitContext } from './contexts/UnitContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildAntdThemeConfig } from './helpers/antdThemeConfig';
import { CustomNavigationClient } from './utils/NavigationClient';
import { MsalProvider } from '@azure/msal-react';
import type { PublicClientApplication } from '@azure/msal-browser';


interface AppProps {
    pca: PublicClientApplication;
}

const App: FC<AppProps> = ({ pca }) => {
    const navigate = useNavigate();

    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    return (
        <MsalProvider instance={pca}>
            <UserProvider>
                <Pages />
            </UserProvider>
        </MsalProvider>
    );
};



const Pages: FC = () => {
    const { theme } = useContext(ThemeContext);
    const { user } = useCurrentUser();
    const { primary } = useContext(ThemeColorContext);
    const unitContext = useContext(UnitContext);

    const isIframeEmbeddedInOneButton = useViewerStore(
        (s) => s.isIframeEmbeddedInOneButton,
    );
    const isViewportOnlyEmbed = useViewerStore((s) => s.isViewportOnlyEmbed);
    const isDashboardOpen = useViewerStore((s) => s.isDashboardOpen);
    const initialDashboardReady = useViewerStore((s: any) => s.initialDashboardReady);

    useEffect(() => {
        useViewerStore.setState({unitContext : unitContext})
    }, [unitContext]);

    useEffect(() => {
        installAiResultCacheTabLifecycle();
    }, []);

    const location = useLocation();
    return (
        <>
            <div
                className='app'
                data-route={location.pathname}
                data-isiframeembeddedinonebutton={isIframeEmbeddedInOneButton}
                data-viewport-embed={isViewportOnlyEmbed ? 'true' : 'false'}
                data-theme={theme}
                data-user={JSON.stringify(user)}
                data-dashboard-open={isDashboardOpen}
                data-dashboard-initialready={initialDashboardReady}
            >
                <ConfigProvider theme={buildAntdThemeConfig(theme, primary)}>
                    <AntdApp>
                        <Routes />
                    </AntdApp>
                </ConfigProvider>
            </div>
        </>
    );
};

export default App;
